// MCHD Room Scheduler - Navigation Configuration
// Role-based navigation items for the MCHD Room Scheduler system

import {
  IconUsers,
  IconBuilding,
  IconCalendar,
  IconClipboardList,
  IconBlockquote,
  IconChartBar,
  IconShield,
  IconSwitchHorizontal,
  IconLogout,
  IconUser,
  IconDeviceDesktop,
  IconFileText,
  IconClock,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { UserRole } from '@mchd/shared/types';

/**
 * Navigation item interface
 */
export interface NavigationItem {
  link: string;
  label: string;
  icon: React.ComponentType<any>;
  roles: UserRole[];
  description?: string;
}

/**
 * Navigation sections interface
 */
export interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

/**
 * MCHD Branding configuration
 */
export const MCHD_BRANDING = {
  name: 'MCHD Scheduler',
  version: 'v1.0.0',
  logo: '/logo/logo-icon-clear.svg', // Use actual MCHD logo
  description: 'Hospital Room Scheduling System',
} as const;

/**
 * Main navigation items for Admin role
 */
const adminNavigationItems: NavigationItem[] = [
  {
    link: '/admin/users',
    label: 'User Management',
    icon: IconUsers,
    roles: ['ADMIN'],
    description: 'Manage user accounts and permissions',
  },
  {
    link: '/admin/buildings',
    label: 'Building Management',
    icon: IconBuilding,
    roles: ['ADMIN'],
    description: 'Manage buildings and infrastructure',
  },
  {
    link: '/admin/rooms',
    label: 'Room Management',
    icon: IconBuilding,
    roles: ['ADMIN'],
    description: 'Configure rooms and room settings',
  },
  {
    link: '/admin/holidays',
    label: 'Holiday Management',
    icon: IconCalendar,
    roles: ['ADMIN'],
    description: 'Set hospital holidays and special dates',
  },
  {
    link: '/admin/session-times',
    label: 'Session Times',
    icon: IconClock,
    roles: ['ADMIN'],
    description: 'Manage room session times and schedules',
  },
  {
    link: '/admin/blocks',
    label: 'Block Management',
    icon: IconBlockquote,
    roles: ['ADMIN'],
    description: 'Manage time blocks and scheduling periods',
  },
  {
    link: '/admin/security',
    label: 'Security Dashboard',
    icon: IconShield,
    roles: ['ADMIN'],
    description: 'Monitor security and system health',
  },
  {
    link: '/admin/sessions',
    label: 'Session Management',
    icon: IconDeviceDesktop,
    roles: ['ADMIN'],
    description: 'Manage user sessions and monitor activity',
  },
  {
    link: '/admin/audit-logs',
    label: 'Audit Logs & Reports',
    icon: IconFileText,
    roles: ['ADMIN'],
    description: 'View audit logs and generate compliance reports',
  },
  {
    link: '/admin/reports',
    label: 'Reports & Analytics',
    icon: IconChartBar,
    roles: ['ADMIN'],
    description: 'View system reports and analytics',
  },
];

/**
 * Main navigation items for Coordinator role
 */
const coordinatorNavigationItems: NavigationItem[] = [
  {
    link: '/planner',
    label: 'Planner',
    icon: IconCalendar,
    roles: ['COORDINATOR'],
    description: 'Interactive room scheduling with grid and floor plan views',
  },
  {
    link: '/coordinator/rooms',
    label: 'Room Management',
    icon: IconBuilding,
    roles: ['COORDINATOR'],
    description: 'View and manage room availability',
  },
        // Session Times removed - now managed within Building Management flow
        {
          link: '/coordinator/blocks',
          label: 'Block Management',
          icon: IconBlockquote,
          roles: ['COORDINATOR'],
          description: 'Manage time blocks and scheduling periods',
        },
  {
    link: '/coordinator/assignments',
    label: 'Assignments',
    icon: IconClipboardList,
    roles: ['COORDINATOR'],
    description: 'Manage room assignments and conflicts',
  },
  {
    link: '/coordinator/dashboard',
    label: 'Dashboard',
    icon: IconChartBar,
    roles: ['COORDINATOR'],
    description: 'Overview of scheduling and utilization',
  },
  {
    link: '/coordinator/users',
    label: 'User Management',
    icon: IconUsers,
    roles: ['COORDINATOR'],
    description: 'Manage user accounts and permissions',
  },
];

/**
 * Main navigation items for Doctor role
 * UX Spec: "Doctor role: Only sees Dashboard and Planner (view-only)"
 * Note: Profile accessible via top bar user avatar dropdown
 */
const doctorNavigationItems: NavigationItem[] = [
  {
    link: '/doctor/dashboard',
    label: 'Dashboard',
    icon: IconChartBar,
    roles: ['DOCTOR'],
    description: 'View your dashboard and upcoming sessions',
  },
  {
    link: '/doctor/schedule',
    label: 'Planner',
    icon: IconCalendar,
    roles: ['DOCTOR'],
    description: 'View your personal schedule (view-only)',
  },
];

/**
 * Footer navigation items (common to all roles)
 */
const footerNavigationItems: NavigationItem[] = [
  {
    link: '/change-account',
    label: 'Change Account',
    icon: IconSwitchHorizontal,
    roles: ['ADMIN', 'COORDINATOR', 'DOCTOR'],
    description: 'Switch between different accounts',
  },
  {
    link: '/logout',
    label: 'Logout',
    icon: IconLogout,
    roles: ['ADMIN', 'COORDINATOR', 'DOCTOR'],
    description: 'Sign out of the system',
  },
];

/**
 * Get navigation items based on user role
 * @param role - User role
 * @returns Array of navigation items for the role
 */
export function getNavigationItems(role: UserRole): NavigationItem[] {
  switch (role) {
    case 'ADMIN':
      return adminNavigationItems;
    case 'COORDINATOR':
      return coordinatorNavigationItems;
    case 'DOCTOR':
      return doctorNavigationItems;
    default:
      return [];
  }
}

/**
 * Get footer navigation items
 * @returns Array of footer navigation items
 */
export function getFooterNavigationItems(): NavigationItem[] {
  return footerNavigationItems;
}

/**
 * Get all navigation sections for a role
 * @param role - User role
 * @returns Array of navigation sections
 */
export function getNavigationSections(role: UserRole): NavigationSection[] {
  const sections: NavigationSection[] = [];

  if (role === 'ADMIN') {
    // Core administration tasks
    sections.push({
      title: 'Core Administration',
      items: [
        {
          link: '/admin/users',
          label: 'User Management',
          icon: IconUsers,
          roles: ['ADMIN'],
          description: 'Manage user accounts and permissions',
        },
        {
          link: '/admin/buildings',
          label: 'Building Management',
          icon: IconBuilding,
          roles: ['ADMIN'],
          description: 'Manage buildings and infrastructure',
        },
        {
          link: '/admin/rooms',
          label: 'Room Management',
          icon: IconBuilding,
          roles: ['ADMIN'],
          description: 'Configure rooms and room settings',
        },
        {
          link: '/admin/holidays',
          label: 'Holiday Management',
          icon: IconCalendar,
          roles: ['ADMIN'],
          description: 'Set hospital holidays and special dates',
        },
        {
          link: '/admin/session-times',
          label: 'Session Times',
          icon: IconClock,
          roles: ['ADMIN'],
          description: 'Manage room session times and schedules',
        },
        {
          link: '/admin/blocks',
          label: 'Block Management',
          icon: IconBlockquote,
          roles: ['ADMIN'],
          description: 'Manage time blocks and scheduling periods',
        },
        {
          link: '/admin/reports',
          label: 'Reports & Analytics',
          icon: IconChartBar,
          roles: ['ADMIN'],
          description: 'View system reports and analytics',
        },
      ],
    });

    // Security and administration tools
    sections.push({
      title: 'Security & Administration',
      items: [
        {
          link: '/admin/security',
          label: 'Security Dashboard',
          icon: IconShield,
          roles: ['ADMIN'],
          description: 'Monitor security and system health',
        },
        {
          link: '/admin/sessions',
          label: 'Session Management',
          icon: IconDeviceDesktop,
          roles: ['ADMIN'],
          description: 'Manage user sessions and monitor activity',
        },
        {
          link: '/admin/audit-logs',
          label: 'Audit Logs & Reports',
          icon: IconFileText,
          roles: ['ADMIN'],
          description: 'View audit logs and generate compliance reports',
        },
      ],
    });
  } else {
    // For non-admin roles, use the flat navigation structure
    sections.push({
      title: 'Main Navigation',
      items: getNavigationItems(role),
    });
  }

  // Account section with logout only
  sections.push({
    title: 'Account',
    items: [
      {
        link: '/logout',
        label: 'Logout',
        icon: IconLogout,
        roles: ['ADMIN', 'COORDINATOR', 'DOCTOR'],
        description: 'Sign out of the system',
      },
    ],
  });

  return sections;
}

/**
 * Check if a navigation item is accessible for a role
 * @param item - Navigation item
 * @param role - User role
 * @returns True if accessible
 */
export function isNavigationItemAccessible(item: NavigationItem, role: UserRole): boolean {
  return item.roles.includes(role);
}

/**
 * Get navigation item by link
 * @param link - Navigation link
 * @param role - User role
 * @returns Navigation item or undefined
 */
export function getNavigationItemByLink(link: string, role: UserRole): NavigationItem | undefined {
  const allItems = [...getNavigationItems(role), ...getFooterNavigationItems()];
  return allItems.find(item => item.link === link);
}
